import { useRef, useState, type DragEvent } from "react";

import { prettifyApiError } from "../../shared/api";
import { propsSenzaPermesso, usePermessi } from "../../shared/auth/permessi";
import { ErrorBanner } from "../../shared/ui/ErrorBanner";
import { MarketplaceLogo } from "../../shared/ui/MarketplaceLogo";
import {
  caricaEstratto,
  type Banca,
  type ContoSaldo,
  type Controllo,
  type EsitoEstratto,
} from "./saldiApi";

/** Davide 02/11/2026: il collegamento automatico alla banca non parte, «per ora
 *  andiamo manualmente». L'estratto conto CoBa Web si carica qui; OM ne legge
 *  gli incassi dei canali, il saldo del conto e i controlli sui soldi. */

export const euro = (valore: string | number, valuta = "EUR") =>
  Number(valore).toLocaleString("it-IT", {
    style: "currency",
    currency: valuta,
    useGrouping: "always" as unknown as boolean,
  });

const giornoLungo = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const momento = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })} alle ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
};

/* ── Il bonifico del canale e' arrivato in banca? ─────────────────────── */

const IN_BANCA = {
  arrivato: { testo: "Arrivato in banca", classe: "border-green/40 bg-green-soft" },
  non_trovato: { testo: "Non arrivato in banca", classe: "border-red/50 bg-red-soft" },
  da_verificare: { testo: "In banca: da verificare", classe: "border-line-2 bg-paper-2" },
} as const;

export function InBanca({ stato }: { stato: ContoSaldo["in_banca"] }) {
  if (stato == null) return null;
  const e = IN_BANCA[stato];
  const spiegazione =
    stato === "arrivato"
      ? "L'ultimo bonifico che il canale dice di aver fatto è nell'estratto conto."
      : stato === "non_trovato"
        ? "Il canale dice di aver pagato, ma nell'estratto conto non c'è un bonifico uguale entro una settimana."
        : "L'estratto conto caricato non arriva ancora a una settimana dopo il bonifico: carica un file più recente.";
  return (
    <span
      title={spiegazione}
      className={`shrink-0 rounded-full border px-2 py-px text-[11px] font-bold text-ink ${e.classe}`}
    >
      {e.testo}
    </span>
  );
}

/* ── Caricamento dell'estratto conto ───────────────────────────────────── */

function useCaricaEstratto(dopo: () => Promise<void>) {
  const [stato, setStato] = useState<"fermo" | "carico">("fermo");
  const [esito, setEsito] = useState<EsitoEstratto | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  async function invia(file: File) {
    setStato("carico");
    setErrore(null);
    setEsito(null);
    try {
      setEsito(await caricaEstratto(file));
      await dopo();
    } catch (err) {
      setErrore(prettifyApiError(err));
    } finally {
      setStato("fermo");
    }
  }
  return { carico: stato === "carico", esito, errore, invia };
}

function TastoCarica({
  carico,
  onFile,
  principale,
}: {
  carico: boolean;
  onFile: (f: File) => void;
  principale?: boolean;
}) {
  const { puoScrivere } = usePermessi();
  const senzaPermesso = propsSenzaPermesso(puoScrivere("fatturazione"), "fatturazione");
  const input = useRef<HTMLInputElement>(null);
  const spento = carico || senzaPermesso.disabled === true;
  return (
    <>
      <button
        type="button"
        disabled={spento}
        onClick={() => input.current?.click()}
        title={
          senzaPermesso.title ?? "Il CSV esportato da CoBa Web, anche sovrapposto ai precedenti"
        }
        className={[
          "inline-flex h-[34px] items-center justify-center gap-1.5 rounded-full border px-3.5 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 disabled:cursor-not-allowed disabled:opacity-50 max-md:h-11 max-md:text-[14px]",
          principale
            ? "border-ink bg-ink text-paper hover:bg-ink/90"
            : "border-line-2 bg-card text-ink hover:border-ink-3 hover:bg-paper-2",
        ].join(" ")}
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          className={`h-[14px] w-[14px] fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round] ${carico ? "animate-pulse" : ""}`}
        >
          <path d="M12 16V4m0 0-4.5 4.5M12 4l4.5 4.5M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
        </svg>
        {carico ? "Leggo il file…" : "Carica estratto conto"}
      </button>
      <input
        ref={input}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        aria-label="Estratto conto CSV"
        disabled={spento}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onFile(file);
          // Senza reset, ricaricare lo stesso file non scatena onChange.
          event.target.value = "";
        }}
      />
    </>
  );
}

function EsitoCaricamento({ esito }: { esito: EsitoEstratto }) {
  return (
    <p role="status" className="mt-3 text-[13px] leading-snug text-ink">
      <b>
        {esito.nuove === 1
          ? "1 movimento nuovo"
          : `${esito.nuove.toLocaleString("it-IT")} movimenti nuovi`}
      </b>
      {esito.gia_viste > 0 && <>, {esito.gia_viste.toLocaleString("it-IT")} già caricati prima</>} ·
      dal {giornoLungo(esito.dal)} al {giornoLungo(esito.al)}
    </p>
  );
}

/** Tutta la scheda si puo' usare come zona di rilascio del file. */
function useRilascio(onFile: (f: File) => void) {
  const [sopra, setSopra] = useState(false);
  return {
    sopra,
    props: {
      onDragOver: (e: DragEvent) => {
        e.preventDefault();
        setSopra(true);
      },
      onDragLeave: () => setSopra(false),
      onDrop: (e: DragEvent) => {
        e.preventDefault();
        setSopra(false);
        const file = e.dataTransfer.files?.[0];
        if (file) onFile(file);
      },
    },
  };
}

/* ── Vista «Banca» ────────────────────────────────────────────────────── */

function ContoInBanca({
  banca,
  carico,
  esito,
  onFile,
}: {
  banca: Banca;
  carico: boolean;
  esito: EsitoEstratto | null;
  onFile: (f: File) => void;
}) {
  const saldo = banca.saldo_contabile != null ? Number(banca.saldo_contabile) : null;
  const fido = banca.fido != null ? Number(banca.fido) : null;
  const usato = saldo != null && saldo < 0 ? -saldo : 0;
  const quota = fido && fido > 0 ? Math.min(usato / fido, 1) : 0;
  const rilascio = useRilascio(onFile);

  return (
    <section
      aria-label="Conto in banca"
      {...rilascio.props}
      className={[
        "mb-7 rounded-lg border bg-card px-5 py-4 transition-colors max-md:px-4",
        rilascio.sopra ? "border-ink border-dashed bg-paper-2" : "border-line",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div className="min-w-0">
          <h2 className="text-[13px] font-bold text-ink">Conto Volksbank</h2>
          <p className="font-display text-[34px] font-bold leading-[1.05] tabular-nums tracking-[-0.02em] text-ink max-md:text-[30px]">
            {saldo != null ? euro(saldo) : "—"}
          </p>
          <p className="mt-1 text-[12.5px] text-ink">
            Saldo contabile{banca.saldo_al && <> al {giornoLungo(banca.saldo_al)}</>}
            {banca.disponibile != null && (
              <>
                {" "}
                · disponibili{" "}
                <span className="font-semibold tabular-nums">{euro(banca.disponibile)}</span>
              </>
            )}
          </p>
          {fido != null && fido > 0 && (
            <div className="mt-3 max-w-[340px]">
              <div
                className="h-[6px] overflow-hidden rounded-full bg-paper-2"
                role="meter"
                aria-label="Fido usato"
                aria-valuemin={0}
                aria-valuemax={fido}
                aria-valuenow={usato}
              >
                <div
                  className={`h-full rounded-full ${quota >= 0.9 ? "bg-red" : quota >= 0.6 ? "bg-amber" : "bg-ink"}`}
                  style={{ width: `${quota * 100}%` }}
                />
              </div>
              <p className="mt-1 text-[12px] tabular-nums text-ink">
                Fido usato <b>{euro(usato)}</b> su {euro(fido)}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-1.5 max-md:w-full max-md:items-stretch">
          <TastoCarica carico={carico} onFile={onFile} />
          <p className="text-right text-[12px] leading-snug text-ink max-md:text-center">
            Ultimo file caricato il {momento(banca.caricato_il)}
            {banca.coperto_fino && (
              <>
                <br />
                movimenti fino al {giornoLungo(banca.coperto_fino)}
              </>
            )}
          </p>
        </div>
      </div>
      {esito && <EsitoCaricamento esito={esito} />}
    </section>
  );
}

const LOGO: Record<string, string> = {
  amazon: "amazon",
  metro: "metro",
  maisons_du_monde: "maisons_du_monde",
  manomano: "manomano",
  vente_unique: "vente_unique",
  bricobravo: "bricobravo",
  shopify_payments: "shopify",
  paypal: "paypal",
};

function IncassiCanali({ banca, conti }: { banca: Banca; conti: ContoSaldo[] }) {
  const logo = (c: Banca["canali"][number]) =>
    conti.find((x) => x.chiave === c.conto)?.logo ?? LOGO[c.chiave] ?? c.chiave;
  return (
    <section aria-labelledby="incassi-titolo" className="mb-7">
      <h2 id="incassi-titolo" className="mb-2 px-1 font-display text-[18px] font-bold text-ink">
        Bonifici arrivati dai canali
      </h2>
      <div className="overflow-hidden rounded-lg border border-line bg-card">
        <table className="w-full border-collapse text-[13px] text-ink">
          <thead>
            <tr className="border-b border-line bg-paper/60 text-left text-[12px] font-bold">
              <th scope="col" className="px-4 py-2.5">
                Canale
              </th>
              <th scope="col" className="px-4 py-2.5 text-right">
                Ultimo bonifico
              </th>
              <th scope="col" className="px-4 py-2.5 text-right max-md:hidden">
                Questo mese
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {banca.canali.map((c) => (
              <tr key={c.chiave} className="transition-colors hover:bg-paper/50">
                <th scope="row" className="px-4 py-3 text-left font-bold">
                  <span className="flex items-center gap-3">
                    <MarketplaceLogo source={logo(c)} label={c.nome} size={26} />
                    <span className="min-w-0 truncate">{c.nome}</span>
                  </span>
                </th>
                <td className="px-4 py-3 text-right tabular-nums">
                  <span className="font-semibold">{euro(c.ultimo_bonifico)}</span>
                  <span className="block whitespace-nowrap text-[12px]">
                    {giornoLungo(c.ultimo_bonifico_il)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums max-md:hidden">
                  {c.bonifici_mese > 0 ? (
                    <>
                      <span className="font-semibold">{euro(c.mese)}</span>
                      <span className="block whitespace-nowrap text-[12px]">
                        {c.bonifici_mese === 1 ? "1 bonifico" : `${c.bonifici_mese} bonifici`}
                      </span>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function NonRiconosciuti({ banca }: { banca: Banca }) {
  if (banca.non_riconosciuti.length === 0 && banca.altre_entrate.bonifici === 0) return null;
  return (
    <section aria-labelledby="nr-titolo">
      <h2 id="nr-titolo" className="mb-1 px-1 font-display text-[18px] font-bold text-ink">
        Entrate che OM non sa a chi dare
      </h2>
      <p className="mb-2 max-w-[70ch] px-1 text-[12.5px] leading-snug text-ink">
        Nessuna regola le riconosce come incasso di un canale, e OM non indovina. Clienti che pagano
        con bonifico, servizi di pagamento nuovi, rimborsi.
      </p>
      {banca.non_riconosciuti.length > 0 && (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-card">
          {banca.non_riconosciuti.map((n) => (
            <li
              key={n.ordinante}
              className="flex items-baseline justify-between gap-4 px-4 py-2.5 text-[13px] text-ink"
            >
              <span className="min-w-0 truncate font-semibold">{n.ordinante || "Senza nome"}</span>
              <span className="shrink-0 text-right tabular-nums">
                <b>{euro(n.totale)}</b> ·{" "}
                {n.bonifici === 1 ? "1 bonifico" : `${n.bonifici} bonifici`}, ultimo il{" "}
                {giornoLungo(n.ultimo_il)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {banca.altre_entrate.bonifici > 0 && (
        <p className="mt-2 px-1 text-[12.5px] text-ink">
          A parte, non vendite: {banca.altre_entrate.bonifici} bonifici per{" "}
          <span className="font-semibold tabular-nums">{euro(banca.altre_entrate.totale)}</span>{" "}
          (note di credito dei fornitori, versamenti dei soci, rimborsi del corriere).
        </p>
      )}
    </section>
  );
}

function PrimoCaricamento({
  carico,
  esito,
  onFile,
}: {
  carico: boolean;
  esito: EsitoEstratto | null;
  onFile: (f: File) => void;
}) {
  const rilascio = useRilascio(onFile);
  return (
    <section
      {...rilascio.props}
      className={[
        "rounded-lg border border-dashed px-6 py-8 text-center transition-colors max-md:px-4",
        rilascio.sopra ? "border-ink bg-paper-2" : "border-line-2 bg-card",
      ].join(" ")}
    >
      <h2 className="font-display text-[20px] font-bold text-ink">
        Carica il primo estratto conto
      </h2>
      <p className="mx-auto mt-2 max-w-[58ch] text-[13px] leading-relaxed text-ink">
        Da CoBa Web esporta i movimenti del conto in <b>CSV</b> e trascina qui il file. OM legge i
        bonifici dei canali, il saldo del conto e controlla doppi pagamenti. Puoi ricaricare file
        che si sovrappongono: i movimenti già visti si saltano.
      </p>
      <div className="mt-5 flex justify-center">
        <TastoCarica carico={carico} onFile={onFile} principale />
      </div>
      {esito && <EsitoCaricamento esito={esito} />}
    </section>
  );
}

export function VistaBanca({
  banca,
  conti,
  ricarica,
}: {
  banca: Banca | null;
  conti: ContoSaldo[];
  ricarica: () => Promise<void>;
}) {
  const { carico, esito, errore, invia } = useCaricaEstratto(ricarica);
  return (
    <>
      <ErrorBanner message={errore} className="mb-3" />
      {banca === null ? (
        <PrimoCaricamento carico={carico} esito={esito} onFile={(f) => void invia(f)} />
      ) : (
        <>
          <ContoInBanca banca={banca} carico={carico} esito={esito} onFile={(f) => void invia(f)} />
          <IncassiCanali banca={banca} conti={conti} />
          <NonRiconosciuti banca={banca} />
        </>
      )}
    </>
  );
}

/* ── Vista «Controlli» ────────────────────────────────────────────────── */

function RigaControllo({ c }: { c: Controllo }) {
  const aperto = c.stato === "da_controllare";
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-6 gap-y-1 px-4 py-3.5 max-md:grid-cols-1 max-md:px-3">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-[14px] font-bold text-ink">
          <span
            className={`shrink-0 rounded-full border px-2 py-px text-[11px] font-bold ${
              aperto ? "border-red/50 bg-red-soft" : "border-green/40 bg-green-soft"
            }`}
          >
            {aperto ? "Da controllare" : "Risolto"}
          </span>
          {c.titolo}
        </p>
        <p className="mt-1 max-w-[80ch] text-[13px] leading-snug text-ink">{c.dettaglio}</p>
        {c.risolto_da && <p className="mt-0.5 text-[12.5px] text-ink">{c.risolto_da}</p>}
      </div>
      <div className="text-right max-md:text-left">
        {c.importo != null && (
          <p
            className={`font-display text-[18px] font-bold tabular-nums text-ink ${aperto ? "" : "line-through decoration-ink/40"}`}
          >
            {euro(c.importo)}
          </p>
        )}
        <p className="text-[12px] text-ink">{giornoLungo(c.data)}</p>
      </div>
    </li>
  );
}

export function VistaControlli({
  controlli,
  conBanca,
}: {
  controlli: Controllo[];
  conBanca: boolean;
}) {
  if (!conBanca) {
    return (
      <p className="rounded-lg border border-line bg-card px-5 py-6 text-[13px] text-ink">
        I controlli partono dall'estratto conto: caricane uno nella scheda <b>Banca</b>.
      </p>
    );
  }
  const aperti = controlli.filter((c) => c.stato === "da_controllare");
  const risolti = controlli.filter((c) => c.stato === "risolto");
  return (
    <>
      <p className="mb-4 max-w-[78ch] px-1 text-[13px] leading-snug text-ink">
        OM confronta i movimenti del conto con ordini e canali e segnala gli errori sui soldi,
        nostri o dei fornitori: lo stesso ordine pagato più volte, pagato al fornitore sbagliato, la
        stessa fattura pagata due volte, un canale che dice di aver pagato ma il bonifico non
        arriva. Non corregge niente da solo.
      </p>
      {aperti.length === 0 ? (
        <p className="mb-7 rounded-lg border border-line bg-card px-5 py-5 text-[13px] font-semibold text-ink">
          Niente da controllare nei movimenti caricati.
        </p>
      ) : (
        <ul className="mb-7 divide-y divide-line overflow-hidden rounded-lg border border-line bg-card">
          {aperti.map((c, i) => (
            <RigaControllo key={`${c.tipo}-${c.data}-${i}`} c={c} />
          ))}
        </ul>
      )}
      {risolti.length > 0 && (
        <section aria-labelledby="risolti-titolo">
          <h2 id="risolti-titolo" className="mb-2 px-1 font-display text-[18px] font-bold text-ink">
            Già sistemati
          </h2>
          <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-card">
            {risolti.map((c, i) => (
              <RigaControllo key={`${c.tipo}-${c.data}-${i}`} c={c} />
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
